/**
 * External API Gateway management — Settings > API Integration tab.
 * Lets a tenant admin issue/rotate/revoke scoped API keys for external
 * agents (voice bots, CRMs, internal tools) calling /api/v1/external/*,
 * and documents that API surface in-app.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Tooltip,
  Stack,
  Autocomplete,
  Select,
  Divider,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  CheckCircle,
  Autorenew,
  Block,
  ExpandMore,
  Key,
  Tune,
} from '@mui/icons-material';
import copy from 'copy-to-clipboard';
import adminApi, {
  ExternalApiClient,
  ExternalApiScope,
  RecordCallbackEventType,
  RecordSubscription,
} from '@/services/admin.api';
import { usePlanGate } from '@/hooks/usePlanGate';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';

const SCOPE_LABELS: Record<ExternalApiScope, string> = {
  send_template: 'Send Template Messages',
  read_status: 'Read Status',
  list_templates: 'List Templates',
  trigger_campaign: 'Trigger Campaigns',
  manage_contacts: 'Manage Contacts',
  manage_leads: 'Manage Leads',
  manage_appointments: 'Manage Appointments',
};

const ALL_SCOPES = Object.keys(SCOPE_LABELS) as ExternalApiScope[];

/** HTTP verb badge. Same restraint as StatusBadge — tinted, not a saturated filled chip, so a
 *  long list of endpoints scans as a document rather than a wall of colour. */
const METHOD_STYLES: Record<string, { bg: string; fg: string }> = {
  GET: { bg: 'rgba(59,130,246,0.10)', fg: 'rgb(29,78,216)' },
  POST: { bg: 'rgba(16,185,129,0.10)', fg: 'rgb(4,120,87)' },
  WEBHOOK: { bg: 'rgba(139,92,246,0.10)', fg: 'rgb(109,40,217)' },
};

/** live/test. `test` is the one worth noticing — it expires — so it carries the tint and `live`
 *  stays neutral, rather than both competing for attention with an outlined chip each. */
const ModeBadge: React.FC<{ mode?: string }> = ({ mode }) => {
  const isTest = mode === 'test';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        bgcolor: isTest ? 'rgba(245,158,11,0.12)' : 'action.hover',
        color: isTest ? 'rgb(146,64,14)' : 'text.secondary',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
      }}
    >
      {mode}
    </Box>
  );
};

const MethodBadge: React.FC<{ method: string }> = ({ method }) => {
  const tone = METHOD_STYLES[method] ?? METHOD_STYLES['GET']!;
  return (
    <Box
      component="span"
      sx={{
        minWidth: 62,
        textAlign: 'center',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        bgcolor: tone.bg,
        color: tone.fg,
        fontFamily: 'monospace',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
      }}
    >
      {method}
    </Box>
  );
};

/**
 * The record-change events an agent can subscribe to, in the order they are shown.
 *
 * `fieldOptions` are suggestions only — the Autocomplete is free-solo, so an admin can name
 * any field path. Only `*.updated` events take a field filter; created/deleted carry no diff,
 * and the API rejects a filter on them rather than storing one that reads as active but isn't.
 */
const RECORD_EVENTS: Array<{
  event: RecordCallbackEventType;
  label: string;
  hint: string;
  group: 'Leads' | 'Appointments';
  fieldOptions?: string[];
  /** Defined in the vocabulary but not emitted yet — rendered disabled. */
  unavailable?: boolean;
}> = [
  {
    event: 'lead.created',
    label: 'Lead created',
    hint: 'A brand-new lead — the first time this person has been seen.',
    group: 'Leads',
  },
  {
    event: 'lead.reengaged',
    label: 'Lead re-engaged',
    hint: 'A lead you already know enquired again — same record, renewed interest. Reported separately from an edit, and never as "created".',
    group: 'Leads',
  },
  {
    event: 'lead.updated',
    label: 'Lead updated',
    hint: 'A field changed on an existing lead. Narrow it with a field filter, or leave empty for any meaningful change.',
    group: 'Leads',
    fieldOptions: [
      'status',
      'assignedTo',
      'tags',
      'customerName',
      'customerPhone',
      'customerEmail',
      'customerCompany',
      'title',
      'description',
      'notes',
      'priority',
      'customAttributes',
    ],
  },
  {
    event: 'lead.deleted',
    label: 'Lead deleted',
    hint: 'A lead was deleted, or merged away into another record.',
    group: 'Leads',
  },
  {
    event: 'appointment.created',
    label: 'Appointment created',
    hint: 'A new appointment was booked, from any channel.',
    group: 'Appointments',
  },
  {
    event: 'appointment.updated',
    label: 'Appointment updated',
    hint: 'Status change, reschedule, reassignment or an edit. Narrow it with a field filter.',
    group: 'Appointments',
    fieldOptions: [
      'status',
      'assignedTo',
      'requestedAt',
      'requestedText',
      'timezone',
      'appointmentType',
      'title',
      'meetingLink',
      'reference',
      'notes',
      'tags',
      'customAttributes',
    ],
  },
  {
    event: 'appointment.deleted',
    label: 'Appointment deleted',
    hint: 'An appointment was deleted.',
    group: 'Appointments',
  },
];

const RECORD_EVENT_GROUPS = ['Leads', 'Appointments'] as const;

/**
 * Status is read constantly and acted on rarely, so it is a quiet tinted badge rather than a
 * saturated filled chip — the same treatment Linear/Stripe/Vercel use for record state. Colour
 * carries meaning but never shouts, and text stays the primary signal so it survives being
 * read by someone who cannot distinguish the hues.
 */
const STATUS_STYLES: Record<string, { bg: string; fg: string }> = {
  active: { bg: 'rgba(16,185,129,0.10)', fg: 'rgb(4,120,87)' },
  suspended: { bg: 'rgba(245,158,11,0.12)', fg: 'rgb(146,64,14)' },
  revoked: { bg: 'rgba(100,116,139,0.12)', fg: 'rgb(71,85,105)' },
};

const StatusBadge: React.FC<{ status?: string | undefined }> = ({ status }) => {
  const tone = STATUS_STYLES[status ?? ''] ?? STATUS_STYLES['revoked']!;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: tone.bg,
        color: tone.fg,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.6,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </Box>
  );
};

/** Test keys carry a 1h expiry; live keys never expire. Returns a small render hint or null. */
function keyExpiry(client: ExternalApiClient): { expired: boolean; label: string } | null {
  if (!client.expiresAt) return null;
  const when = new Date(client.expiresAt);
  const expired = when.getTime() < Date.now();
  return { expired, label: expired ? 'Expired' : `Expires ${when.toLocaleString()}` };
}

interface ApiEndpointDoc {
  method: 'GET' | 'POST';
  path: string;
  scope: ExternalApiScope | null;
  description: string;
  request?: string;
  response: string;
}

const API_ENDPOINTS: ApiEndpointDoc[] = [
  {
    method: 'GET',
    path: '/health',
    scope: null,
    description: 'Verify your API key resolves correctly, with zero side effects.',
    response: `{
  "success": true,
  "data": { "status": "ok", "clientId": "...", "tenantId": "..." }
}`,
  },
  {
    method: 'GET',
    path: '/templates',
    scope: 'list_templates',
    description:
      'List WhatsApp templates approved and available for sending. Check components.buttons for a type: "URL" button whose url ends in {{1}} — that\'s how you tell whether a template needs buttonUrlVariable on POST /messages below (AUTHENTICATION templates never need it, even if they have one). Check components.header.type: a value of IMAGE, VIDEO or DOCUMENT means the template carries a file in its header, so POST /messages needs headerMediaUrl (a TEXT header, or no header, carries no file).',
    response: `{
  "success": true,
  "data": [
    { "name": "order_confirmation", "language": "en_US", "category": "UTILITY", "status": "APPROVED",
      "components": { "body": { "text": "Hi {{1}}, your order {{2}} shipped." } } },
    { "name": "invoice_ready", "language": "en_US", "category": "UTILITY", "status": "APPROVED",
      "components": {
        "body": { "text": "Hi {{1}}, your invoice is ready." },
        "buttons": [{ "type": "URL", "text": "Visit website", "url": "https://example.com/invoice/{{1}}" }]
      } },
    { "name": "ticket_confirmation", "language": "en_US", "category": "UTILITY", "status": "APPROVED",
      "components": {
        "header": { "type": "DOCUMENT" },
        "body": { "text": "Hi {{1}}, your ticket is attached." }
      } }
  ]
}`,
  },
  {
    method: 'POST',
    path: '/messages',
    scope: 'send_template',
    description:
      'Send a single approved WhatsApp template message (adhoc lane). If the template has a dynamic URL button (a "Visit website"/"Download"-style button whose URL ends in {{1}} — check GET /templates for components.buttons) and isn\'t an AUTHENTICATION template, buttonUrlVariable is required and must be the exact value for that slot, not the full URL. AUTHENTICATION templates resolve it automatically from the OTP code; sending it for a template that has no dynamic URL button at all is accepted and ignored. If the template has an IMAGE/VIDEO/DOCUMENT header (check GET /templates for components.header.type), pass headerMediaUrl: a public http(s) URL of the file for this send, whose type must match the approved header. It is required only when the admin set no default media on the template; the URL must stay publicly reachable (WhatsApp fetches it on send, and it is what renders in Chat Logs). Sending it for a template with no media header is accepted and ignored.',
    request: `{
  "toPhone": "+15551234567",
  "templateName": "order_confirmation",
  "templateLanguage": "en_US",
  "variables": ["John", "12345"],
  // buttonUrlVariable is OPTIONAL — omit entirely unless this template has a
  // dynamic URL button and isn't AUTHENTICATION (see description above).
  "buttonUrlVariable": "inv-98214",
  // headerMediaUrl is OPTIONAL — include only for an IMAGE/VIDEO/DOCUMENT-header
  // template (public http(s) URL, type must match the approved header).
  "headerMediaUrl": "https://files.example.com/tickets/john-98214.pdf",
  "idempotencyKey": "optional-unique-key"
}`,
    response: `{
  "success": true,
  "data": { "requestId": "..." }
}`,
  },
  {
    method: 'GET',
    path: '/messages/:requestId',
    scope: 'read_status',
    description: 'Poll the delivery status of a previously sent message.',
    response: `{
  "success": true,
  "data": {
    "requestId": "...", "status": "delivered", "toPhone": "...",
    "templateName": "...", "whatsappMessageId": "...",
    "sentAt": "...", "deliveredAt": "..."
  }
}`,
  },
  {
    method: 'GET',
    path: '/campaigns',
    scope: 'trigger_campaign',
    description:
      "List this tenant's campaigns available to trigger (discovery step before triggering one).",
    response: `{
  "success": true,
  "data": [
    {
      "id": "...", "name": "Spring Promo", "status": "active", "type": "bulk",
      "triggerType": "manual", "maxRecipients": 1000, "createdAt": "..."
    }
  ]
}`,
  },
  {
    method: 'POST',
    path: '/campaigns/:id/trigger',
    scope: 'trigger_campaign',
    description:
      "Trigger an existing, tenant-created campaign. Omit targetPhones to use the campaign's own saved audience.",
    request: `{
  "targetPhones": ["+15551234567", "+15557654321"]
}`,
    response: `{
  "success": true,
  "data": { "started": true, "total": 2, "sent": 0, "failed": 0, "skipped": 0, "runId": "..." }
}`,
  },
  {
    method: 'GET',
    path: '/campaigns/:id/status',
    scope: 'read_status',
    description: 'Read aggregate progress for a triggered campaign run.',
    response: `{
  "success": true,
  "data": {
    "runId": "...", "status": "in_progress",
    "totals": { "targeted": 1000, "sent": 400, "delivered": 350, "failed": 2, "skipped": 0 },
    // present only once totals.failed > 0 — see the Webhooks section below for the format
    "failureReason": "2 recipient(s) failed: (#131030) Recipient phone number not in allowed list",
    "failureBreakdown": [{ "code": 131030, "message": "(#131030) Recipient phone number not in allowed list", "count": 2 }]
  }
}`,
  },
  {
    method: 'POST',
    path: '/contacts',
    scope: 'manage_contacts',
    description:
      'Create or update a single contact — matched by phone number. Tags are stored as-is (shared tenant vocabulary, not prefixed with your agent name); this contact shows your agent\'s name as its Source in the admin UI. For more than one contact, prefer /contacts/bulk-upsert below: one call there costs one rate-limit token no matter how many contacts it contains.',
    request: `{
  "phone": "+15551234567", "name": "John Doe",
  "email": "john@example.com", "countryCode": "US",
  "tags": ["premium"], "attributes": { "plan": "pro" },
  "externalContactId": "ext-123"
}`,
    response: `{
  "success": true,
  "data": { "created": true, "contactId": "..." }
}`,
  },
  {
    method: 'POST',
    path: '/contacts/bulk-upsert',
    scope: 'manage_contacts',
    description: "Bulk create/update contacts and attach your agent's own tags and attributes.",
    request: `{
  "contacts": [
    {
      "phone": "+15551234567", "name": "John Doe",
      "email": "john@example.com", "countryCode": "US",
      "tags": ["premium"], "attributes": { "plan": "pro" },
      "externalContactId": "ext-123"
    }
  ]
}`,
    response: `{
  "success": true,
  "data": { "total": 1, "created": 1, "updated": 0, "skipped": 0, "errors": [] }
}`,
  },
  {
    method: 'POST',
    path: '/leads',
    scope: 'manage_leads',
    description:
      'Create or update a single lead — matched by phone if given, else by email (at least one is required). Tags are stored as-is (shared tenant vocabulary, not prefixed with your agent name); this lead shows your agent\'s name as its Source in the admin UI.',
    request: `{
  "phone": "+15551234567", "email": "jane@example.com", "name": "Jane Doe",
  "title": "Website pricing enquiry", "description": "Asked about enterprise pricing.",
  "tags": ["hot-lead"], "attributes": { "campaign": "spring-promo" },
  "externalLeadId": "ext-456"
}`,
    response: `{
  "success": true,
  "data": { "created": true, "leadId": "..." }
}`,
  },
  {
    method: 'POST',
    path: '/leads/bulk-upsert',
    scope: 'manage_leads',
    description: "Bulk create/update leads and attach your agent's own tags and attributes.",
    request: `{
  "leads": [
    {
      "phone": "+15551234567", "name": "Jane Doe",
      "title": "Website pricing enquiry",
      "tags": ["hot-lead"], "attributes": { "campaign": "spring-promo" },
      "externalLeadId": "ext-456"
    }
  ]
}`,
    response: `{
  "success": true,
  "data": { "total": 1, "created": 1, "updated": 0, "skipped": 0, "errors": [] }
}`,
  },
  {
    method: 'POST',
    path: '/appointments',
    scope: 'manage_appointments',
    description:
      'Create or update a single appointment. Only "phone" and one of "requestedAt"/"requestedText" are required — everything else is optional (a trailing "?" on a field name below marks it optional; omit the field entirely, don\'t send the "?"). Pass "externalAppointmentId" to make repeat calls idempotent (a second call with the same id updates that appointment, e.g. a status change or reschedule); omitting it always creates a new appointment, since a customer may legitimately book more than once.',
    request: `{
  "phone": "+15551234567", "name?": "Jane Doe", "email?": "jane@example.com",
  "requestedAt?": "2026-09-10T14:00:00+05:30", "requestedText?": "next Monday afternoon",
  "timezone?": "Asia/Kolkata", "title?": "Renewal discussion", "status?": "confirmed",
  "meetingLink?": "https://meet.google.com/abc-defg-hij",
  "reference?": "BOOK-9821", "notes?": "Prefers a callback first",
  "tags?": ["vip"], "attributes?": { "plan": "pro" },
  "externalAppointmentId?": "ext-789"
}`,
    response: `{
  "success": true,
  "data": { "created": true, "appointmentId": "..." }
}`,
  },
  {
    method: 'POST',
    path: '/appointments/bulk-upsert',
    scope: 'manage_appointments',
    description: "Bulk create/update appointments and attach your agent's own tags and attributes. Rows with an \"externalAppointmentId\" are upserted by that id; rows without one are always inserted as new. Same \"field?\" = optional notation as the single-appointment endpoint above.",
    request: `{
  "appointments": [
    {
      "phone": "+15551234567", "requestedText?": "next Monday afternoon",
      "title?": "Renewal discussion", "tags?": ["vip"], "externalAppointmentId?": "ext-789"
    }
  ]
}`,
    response: `{
  "success": true,
  "data": { "total": 1, "created": 1, "updated": 0, "skipped": 0, "errors": [] }
}`,
  },
];

type ConfirmAction = {
  type: 'rotate' | 'revoke' | 'regenerate-callback-secret';
  client: ExternalApiClient;
};

const ExternalGatewayConfig: React.FC = () => {
  const { guard } = usePlanGate();
  const [subTab, setSubTab] = useState(0);
  // Revoked agents are kept for the audit trail but are dead weight in the working view, so the
  // list defaults to Active and they are one click away rather than interleaved.
  const [statusFilter, setStatusFilter] = useState<'active' | 'revoked' | 'all'>('active');
  const [clients, setClients] = useState<ExternalApiClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    label: string;
    mode: 'live' | 'test';
    scopes: ExternalApiScope[];
    callbackUrl: string;
  }>({ name: '', label: '', mode: 'live', scopes: [], callbackUrl: '' });

  const [revealOpen, setRevealOpen] = useState(false);
  const [reveal, setReveal] = useState<{
    apiKey?: string;
    callbackSigningSecret?: string;
    expiresAt?: string;
  } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // One panel drives all per-agent editing; the per-section state below is its form state.
  const [manageClient, setManageClient] = useState<ExternalApiClient | null>(null);
  const [manageTab, setManageTab] = useState(0);

  const openManage = (client: ExternalApiClient, tab = 0) => {
    setManageClient(client);
    setManageTab(tab);
    setEditCallbackUrl(client.callbackUrl ?? '');
    setEditScopesValue(client.scopes);
    setEditEventsValue((client.recordSubscriptions ?? []).map((sub) => ({ ...sub })));
    setEditSuppressSelf(Boolean(client.suppressSelfCausedEvents));
  };
  const closeManage = () => setManageClient(null);
  const [editCallbackUrl, setEditCallbackUrl] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editEventsValue, setEditEventsValue] = useState<RecordSubscription[]>([]);
  const [editEventsSaving, setEditEventsSaving] = useState(false);
  const [editSuppressSelf, setEditSuppressSelf] = useState(false);
  const [editScopesValue, setEditScopesValue] = useState<ExternalApiScope[]>([]);
  const [editScopesSaving, setEditScopesSaving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getExternalApiClients();
      setClients(data);
    } catch {
      setError('Failed to load integrated CRMs');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    copy(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const resetForm = () =>
    setForm({ name: '', label: '', mode: 'live', scopes: [], callbackUrl: '' });

  const toggleScope = (scope: ExternalApiScope) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(scope) ? f.scopes.filter((s) => s !== scope) : [...f.scopes, scope],
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim() || form.scopes.length === 0) return;
    try {
      setCreating(true);
      setError('');
      const label = form.label.trim();
      const callbackUrl = form.callbackUrl.trim();
      const result = await adminApi.createExternalApiClient({
        name: form.name.trim(),
        mode: form.mode,
        scopes: form.scopes,
        ...(label && { label }),
        ...(callbackUrl && { callbackUrl }),
      });
      setCreateOpen(false);
      resetForm();
      const expiresAt = result.client?.expiresAt;
      setReveal({
        apiKey: result.apiKey,
        ...(result.callbackSigningSecret
          ? { callbackSigningSecret: result.callbackSigningSecret }
          : {}),
        ...(expiresAt ? { expiresAt } : {}),
      });
      setRevealOpen(true);
      await fetchClients();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(err?.response?.data?.error?.message || 'Failed to create integrated CRM');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    try {
      setActionLoading(true);
      setError('');
      if (confirmAction.type === 'rotate') {
        const result = await adminApi.rotateExternalApiClient(confirmAction.client._id);
        const expiresAt = result.client?.expiresAt;
        setReveal({ apiKey: result.apiKey, ...(expiresAt ? { expiresAt } : {}) });
        setRevealOpen(true);
      } else if (confirmAction.type === 'regenerate-callback-secret') {
        const result = await adminApi.regenerateExternalApiClientCallbackSecret(
          confirmAction.client._id
        );
        setReveal({ callbackSigningSecret: result.callbackSigningSecret });
        setRevealOpen(true);
      } else {
        await adminApi.revokeExternalApiClient(confirmAction.client._id);
      }
      await fetchClients();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(`Failed to ${confirmAction.type} "${confirmAction.client.name}"`);
      }
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleSaveCallback = async () => {
    const editClient = manageClient;
    if (!editClient) return;
    try {
      setEditSaving(true);
      setError('');
      const trimmed = editCallbackUrl.trim();
      const result = await adminApi.updateExternalApiClientCallback(
        editClient._id,
        trimmed || null
      );
      if (result.callbackSigningSecret) {
        // A brand-new signing secret is shown exactly once, so the panel gets out of its way.
        closeManage();
        setReveal({ callbackSigningSecret: result.callbackSigningSecret });
        setRevealOpen(true);
      } else {
        // Otherwise stay on the tab: saving a URL is usually step one of "now let me pick the
        // events", and reflecting it locally unlocks that section immediately instead of making
        // the user reopen the panel. Clearing the URL also clears subscriptions server-side, so
        // mirror that here rather than showing a stale list.
        // Rebuilt rather than spread-with-undefined: exactOptionalPropertyTypes treats
        // `callbackUrl: undefined` as different from an absent key.
        setManageClient((c) => {
          if (!c) return c;
          const { callbackUrl: _drop, ...rest } = c;
          return trimmed ? { ...rest, callbackUrl: trimmed } : rest;
        });
        if (!trimmed) setEditEventsValue([]);
      }
      await fetchClients();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(err?.response?.data?.error?.message || 'Failed to update callback URL');
      }
    } finally {
      setEditSaving(false);
    }
  };

  const eventSub = (event: RecordCallbackEventType): RecordSubscription | undefined =>
    editEventsValue.find((sub) => sub.event === event);

  const toggleEvent = (event: RecordCallbackEventType) => {
    setEditEventsValue((subs) =>
      subs.some((sub) => sub.event === event)
        ? subs.filter((sub) => sub.event !== event)
        : // 'own' is the safe default: it delivers only records this agent pushed or was
          // told about. 'all' mirrors the tenant's entire stream and is an explicit choice.
          [...subs, { event, scope: 'own' as const }]
    );
  };

  const patchEvent = (event: RecordCallbackEventType, patch: Partial<RecordSubscription>) => {
    setEditEventsValue((subs) =>
      subs.map((sub) => (sub.event === event ? { ...sub, ...patch } : sub))
    );
  };

  const handleSaveEvents = async () => {
    const editEventsClient = manageClient;
    if (!editEventsClient) return;
    try {
      setEditEventsSaving(true);
      setError('');
      await adminApi.updateExternalApiClientRecordSubscriptions(
        editEventsClient._id,
        // Drop an empty `fields` array so the stored shape means "any field" rather than
        // "a filter that matches nothing".
        editEventsValue.map((sub) => ({
          event: sub.event,
          scope: sub.scope,
          ...(sub.fields && sub.fields.length ? { fields: sub.fields } : {}),
        })),
        editSuppressSelf
      );
      closeManage();
      await fetchClients();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(err?.response?.data?.error?.message || 'Failed to update event subscriptions');
      }
    } finally {
      setEditEventsSaving(false);
    }
  };

  const toggleEditScope = (scope: ExternalApiScope) => {
    setEditScopesValue((s) =>
      s.includes(scope) ? s.filter((v) => v !== scope) : [...s, scope]
    );
  };

  const handleSaveScopes = async () => {
    const editScopesClient = manageClient;
    if (!editScopesClient || editScopesValue.length === 0) return;
    try {
      setEditScopesSaving(true);
      setError('');
      await adminApi.updateExternalApiClientScopes(editScopesClient._id, editScopesValue);
      closeManage();
      await fetchClients();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(err?.response?.data?.error?.message || 'Failed to update scopes');
      }
    } finally {
      setEditScopesSaving(false);
    }
  };

  // `revoked` is terminal; everything else (active/suspended) is still a working integration.
  const statusCounts = {
    active: clients.filter((c) => c.status !== 'revoked').length,
    revoked: clients.filter((c) => c.status === 'revoked').length,
  };
  const visibleClients =
    statusFilter === 'all'
      ? clients
      : statusFilter === 'revoked'
        ? clients.filter((c) => c.status === 'revoked')
        : clients.filter((c) => c.status !== 'revoked');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Integrated CRMs" />
        <Tab label="API Reference" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {subTab === 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
            <Typography variant="body2" color="text.secondary">
              External systems (CRMs, voice bots, internal tools) that call your API with a scoped key.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={guard('apiAccess', () => setCreateOpen(true))}>
              New CRM
            </Button>
          </Box>

          {clients.length > 0 && (
            <Tabs
              value={statusFilter}
              onChange={(_, v) => setStatusFilter(v)}
              sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
            >
              <Tab value="active" label={`Active (${statusCounts.active})`} />
              <Tab value="revoked" label={`Revoked (${statusCounts.revoked})`} />
              <Tab value="all" label={`All (${clients.length})`} />
            </Tabs>
          )}

          {clients.length === 0 ? (
            <Card>
              <CardContent>
                <Box textAlign="center" py={4}>
                  <Key sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    No integrated CRMs yet. Create one to issue an API key.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Card}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>CRM</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Callback</TableCell>
                    <TableCell>Events</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleClients.map((client) => (
                    <TableRow key={client._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {client.name}
                        </Typography>
                        {client.label && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {client.label}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <ModeBadge mode={client.mode} />
                          {(() => {
                            const exp = keyExpiry(client);
                            if (!exp) return null;
                            // Expiry sits beside the mode, not stacked under it in red — a test
                            // key lapsing is expected housekeeping, not an error state, and a
                            // two-line cell made every test row taller than every live one.
                            return (
                              <Tooltip title={exp.label}>
                                <Typography
                                  variant="caption"
                                  color={exp.expired ? 'error.main' : 'text.secondary'}
                                >
                                  {exp.expired ? 'expired' : exp.label}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {client.callbackUrl ? (
                          <Tooltip title={client.callbackUrl}>
                            <Typography variant="body2" color="text.primary">
                              Configured
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.recordSubscriptions?.length ? (
                          <Tooltip
                            title={
                              <Box>
                                {client.recordSubscriptions.map((sub) => (
                                  <Typography key={sub.event} variant="caption" display="block">
                                    {sub.event} —{' '}
                                    {sub.scope === 'all' ? 'all records' : "this CRM's records"}
                                    {sub.fields?.length ? ` · fields: ${sub.fields.join(', ')}` : ''}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <Typography variant="body2" color="text.primary">
                              {client.recordSubscriptions.length} subscribed
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={client.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {client.lastUsedAt
                            ? new Date(client.lastUsedAt).toLocaleString()
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {/* One entry point per agent. Four separate icons (scopes / callback /
                            rotate / revoke) made the destructive action a peer of the routine
                            ones and gave no room to label anything — everything now lives in a
                            tabbed panel, which is how Stripe and Twilio present an integration. */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Tune fontSize="small" />}
                          disabled={client.status === 'revoked'}
                          onClick={guard('apiAccess', () => openManage(client))}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {subTab === 1 && (
        <Box>
          <Box mb={3} p={2} border={1} borderColor="divider" borderRadius={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Base URL
            </Typography>
            <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
              {`${window.location.origin}/api/v1/external`}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Every request needs the header{' '}
              <Box component="span" fontFamily="monospace">
                X-Api-Key: &lt;your key&gt;
              </Box>
            </Typography>
          </Box>

          {/* The five things an integrator is handed. Definitions live HERE, in the reference,
              rather than as helper text scattered through the setup forms — a form should be
              fillable at a glance, and this is the place someone actually looks things up. */}
          <Box mb={3} p={2} border={1} borderColor="divider" borderRadius={2}>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              Concepts
            </Typography>
            <Stack spacing={1.25}>
              {[
                {
                  term: 'API key',
                  desc: 'Authenticates every request. Shown once when created or rotated — store it then, we cannot show it again.',
                },
                {
                  term: 'Scopes',
                  desc: 'What a key is allowed to do. A call outside its scopes returns 403.',
                },
                {
                  term: 'Callback URL',
                  desc: 'Where we POST events to you. Optional — without it you poll instead.',
                },
                {
                  term: 'Signing secret',
                  desc: 'Issued with the callback URL. Use it to verify the X-Webhook-Signature header so you know a callback is really from us.',
                },
                {
                  term: 'Event subscriptions',
                  desc: 'Which lead and appointment changes we push. Opt-in per event, scoped to records related to you or all of them; delivery callbacks are always on.',
                },
              ].map((row) => (
                <Box key={row.term}>
                  <Typography variant="body2" fontWeight={600} component="span">
                    {row.term}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="span">
                    {' — '}
                    {row.desc}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {API_ENDPOINTS.map((ep) => (
            <Accordion key={`${ep.method}-${ep.path}`} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  <MethodBadge method={ep.method} />
                  <Typography fontFamily="monospace" fontWeight={600}>
                    {ep.path}
                  </Typography>
                  {ep.scope && (
                    <Typography variant="caption" color="text.secondary">
                      {SCOPE_LABELS[ep.scope]}
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {ep.description}
                </Typography>
                {ep.request && (
                  <>
                    <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                      REQUEST BODY
                    </Typography>
                    <Box
                      component="pre"
                      p={1.5}
                      bgcolor="grey.50"
                      color="text.primary"
                      border={1}
                      borderColor="divider"
                      borderRadius={1}
                      fontSize={12}
                      overflow="auto"
                      mb={2}
                    >
                      {ep.request}
                    </Box>
                  </>
                )}
                <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                  RESPONSE
                </Typography>
                <Box
                  component="pre"
                  p={1.5}
                  bgcolor="grey.50"
                  color="text.primary"
                  border={1}
                  borderColor="divider"
                  borderRadius={1}
                  fontSize={12}
                  overflow="auto"
                >
                  {ep.response}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>
            Webhooks (chatbase → your agent)
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Everything above is a call <em>you</em> make to chatbase. The other direction — chatbase
            pushing delivery/status updates to <em>you</em> — only happens if the agent has a
            Callback URL set (see the Callback column on the Integrated CRMs tab). It is entirely
            optional: you can always fall back to polling{' '}
            <Box component="span" fontFamily="monospace" fontSize={13}>
              GET /messages/:requestId
            </Box>{' '}
            or{' '}
            <Box component="span" fontFamily="monospace" fontSize={13}>
              GET /campaigns/:id/status
            </Box>{' '}
            instead.
          </Typography>

          <Accordion defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <MethodBadge method="WEBHOOK" />
                <Typography fontFamily="monospace" fontWeight={600}>
                  POST &lt;your callbackUrl&gt;
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Always on: <strong>message.status_update</strong>,{' '}
                <strong>campaign.progress</strong>, <strong>campaign.completed</strong>.
                Opt-in per CRM: <strong>lead.created</strong>, <strong>lead.reengaged</strong>,{' '}
                <strong>lead.updated</strong>, <strong>lead.deleted</strong>,{' '}
                <strong>appointment.created</strong>, <strong>appointment.updated</strong>,{' '}
                <strong>appointment.deleted</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Retried up to 6 times with exponential backoff; if every attempt fails a dashboard
                alert is raised. Deduplicate on the envelope&apos;s <code>id</code> — it is stable
                across retries.
              </Typography>

              <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                HEADERS
              </Typography>
              <Box
                component="pre"
                p={1.5}
                bgcolor="grey.50"
                color="text.primary"
                border={1}
                borderColor="divider"
                borderRadius={1}
                fontSize={12}
                overflow="auto"
                mb={2}
              >
                {`Content-Type: application/json
X-Webhook-Id:        <uuid>            # stable across retries — dedupe on this
X-Webhook-Event:     appointment.updated
X-Webhook-Signature: t=<unix_seconds>,v1=<hmac>,v2=<hmac>

# v2 (use this) = HMAC-SHA256 of "<id>.<t>.<raw body>"
# v1 (deprecated) = HMAC-SHA256 of "<t>.<raw body>"
# Key both with the callback signing secret. Reject a timestamp older than a few minutes.`}
              </Box>

              <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                BODY — message.status_update (adhoc lane)
              </Typography>
              <Box
                component="pre"
                p={1.5}
                bgcolor="grey.50"
                color="text.primary"
                border={1}
                borderColor="divider"
                borderRadius={1}
                fontSize={12}
                overflow="auto"
                mb={2}
              >
                {`{
  "id": "0f8b1c2e-...",
  "event": "message.status_update",
  "timestamp": "2026-06-19T10:00:00.000Z",
  "data": {
    "requestId": "...",
    "status": "sent" | "delivered" | "read" | "failed",
    "toPhone": "+15551234567",
    "templateName": "order_update",
    "whatsappMessageId": "wamid....",
    "error": "..." // present only when status is "failed"
  }
}`}
              </Box>

              <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                BODY — campaign.progress / campaign.completed (bulk lane)
              </Typography>
              <Box
                component="pre"
                p={1.5}
                bgcolor="grey.50"
                color="text.primary"
                border={1}
                borderColor="divider"
                borderRadius={1}
                fontSize={12}
                overflow="auto"
                mb={2}
              >
                {`{
  "id": "0f8b1c2e-...",
  "event": "campaign.progress" | "campaign.completed",
  "timestamp": "2026-06-19T10:00:05.000Z",
  "data": {
    "runId": "...",
    "campaignId": "...",
    "status": "in_progress" | "completed" | "partial" | "failed",
    "totals": { "targeted": 1000, "sent": 640, "delivered": 530, "failed": 12, "skipped": 0 },
    // present only when totals.failed > 0 (status "partial" or "failed") — a one-line
    // summary plus the top distinct error reasons by recipient count, so a "failed" run
    // can be told apart from a "partial" one without re-querying every recipient
    "failureReason": "12 recipient(s) failed: Authentication Error",
    "failureBreakdown": [
      { "code": 190, "message": "Authentication Error", "count": 10 },
      { "code": 131026, "message": "Recipient phone number is not a valid WhatsApp user", "count": 2 }
    ]
  }
}`}
              </Box>

              <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                VERIFYING THE SIGNATURE
              </Typography>
              <Box
                component="pre"
                p={1.5}
                bgcolor="grey.50"
                color="text.primary"
                border={1}
                borderColor="divider"
                borderRadius={1}
                fontSize={12}
                overflow="auto"
                mb={2}
              >
                {`// secret = the callbackSigningSecret shown once when the agent was created
const [t, v1] = signatureHeader.replace('t=', '').replace('v1=', '').split(',').map(p => p.split('=').pop());
const expected = crypto.createHmac('sha256', secret).update(\`\${t}.\${rawBody}\`).digest('hex');
// compare \`expected\` to v1 with a constant-time comparison (e.g. crypto.timingSafeEqual)`}
              </Box>

              <Typography variant="caption" color="text.secondary" display="block">
                <strong>What URL to use:</strong> this must be reachable from this server, not from
                your browser. If your agent runs on the same machine as this backend (e.g. local
                development), <code>http://localhost:PORT/...</code> works as-is. Once this backend
                and your agent are on different machines — including testing a locally-running agent
                against an already-deployed backend — your agent's URL has to be one this server can
                reach over the network: either a real <code>https://</code> endpoint if your agent
                is itself deployed, or a tunnel (e.g. ngrok) exposing your local port if it's still
                only running on your laptop. In real production, the external agent is normally
                deployed on its own infrastructure, so it just uses its own permanent{' '}
                <code>https://</code> URL — tunnels are a local-development convenience, not a
                production requirement.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Integrated CRM</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Name"
              required
              fullWidth
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Voice Agent Platform"
            />
            <TextField
              label="Label (optional)"
              fullWidth
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="Internal description"
            />
            <TextField
              select
              label="Mode"
              fullWidth
              value={form.mode}
              onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value as 'live' | 'test' }))}
              helperText={
                form.mode === 'test'
                  ? 'Test keys expire 1 hour after they are created (and the window restarts each time you rotate). Use them for short-lived integration testing — they call the same live endpoints, so a test key still sends real messages and writes real data while it is valid.'
                  : 'Live keys never expire.'
              }
            >
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </TextField>
            <Box>
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                Scopes
              </Typography>
              <FormGroup>
                {ALL_SCOPES.map((scope) => (
                  <FormControlLabel
                    key={scope}
                    control={
                      <Checkbox
                        checked={form.scopes.includes(scope)}
                        onChange={() => toggleScope(scope)}
                      />
                    }
                    label={SCOPE_LABELS[scope]}
                  />
                ))}
              </FormGroup>
            </Box>
            <TextField
              label="Callback URL (optional)"
              fullWidth
              value={form.callbackUrl}
              onChange={(e) => setForm((f) => ({ ...f, callbackUrl: e.target.value }))}
              placeholder="https://your-agent.example.com/webhooks/whatsapp"
              helperText="If set, we'll sign delivery/status callbacks with a secret you'll receive once. Must be reachable from this server: a real https:// endpoint in production, or a tunnel (e.g. ngrok) if your agent runs locally while this backend doesn't. See the API Reference tab for the payload format."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={guard('apiAccess', handleCreate)}
            disabled={creating || !form.name.trim() || form.scopes.length === 0}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret reveal dialog */}
      <Dialog open={revealOpen} onClose={() => setRevealOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reveal?.apiKey ? 'Save your API key' : 'Save your callback signing secret'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the secret is shown. Copy it now — it cannot be retrieved later,
            only rotated.
          </Alert>
          {reveal?.expiresAt && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a <strong>test</strong> key — it stops working at{' '}
              {new Date(reveal.expiresAt).toLocaleString()} (1 hour from now). Create a Live key for
              anything long-lived.
            </Alert>
          )}
          {reveal?.apiKey && (
            <>
              <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                API KEY
              </Typography>
              <Box display="flex" gap={1} mb={reveal?.callbackSigningSecret ? 2 : 0}>
                <TextField
                  fullWidth
                  size="small"
                  value={reveal?.apiKey ?? ''}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={copiedField === 'apiKey' ? <CheckCircle /> : <ContentCopy />}
                  onClick={() => handleCopy(reveal?.apiKey ?? '', 'apiKey')}
                >
                  {copiedField === 'apiKey' ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
            </>
          )}
          {reveal?.callbackSigningSecret && (
            <>
              <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                CALLBACK SIGNING SECRET
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={reveal.callbackSigningSecret}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={copiedField === 'callbackSecret' ? <CheckCircle /> : <ContentCopy />}
                  onClick={() => handleCopy(reveal.callbackSigningSecret ?? '', 'callbackSecret')}
                >
                  {copiedField === 'callbackSecret' ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setRevealOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Unified per-agent management panel ───────────────────────────────
          One entry point instead of four row icons. Grouped the way an integrator actually
          thinks about an integration — what it may DO, where we CALL it, and its CREDENTIALS —
          the same split Stripe/Twilio/GitHub use for an app or endpoint detail view.
          Callback URL and event subscriptions deliberately share a tab: a subscription with no
          URL has nowhere to deliver and the API rejects it, so splitting them would let a user
          build an invalid state across two screens. */}
      <Dialog
        open={!!manageClient}
        onClose={() => !editSaving && !editScopesSaving && !editEventsSaving && closeManage()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" component="span">
              {manageClient?.label || manageClient?.name}
            </Typography>
            <Chip label={manageClient?.mode} size="small" variant="outlined" />
            <StatusBadge status={manageClient?.status} />
          </Stack>
        </DialogTitle>

        <Tabs
          value={manageTab}
          onChange={(_, v) => setManageTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        >
          <Tab label="Permissions" />
          <Tab label="Callbacks & events" />
          <Tab label="Keys & security" />
        </Tabs>

        <DialogContent dividers sx={{ minHeight: 420 }}>
          {manageTab === 0 && (
            <Box>
              <FormGroup>
                {ALL_SCOPES.map((scope) => (
                  <FormControlLabel
                    key={scope}
                    control={
                      <Checkbox
                        checked={editScopesValue.includes(scope)}
                        onChange={() => toggleEditScope(scope)}
                      />
                    }
                    label={SCOPE_LABELS[scope]}
                  />
                ))}
              </FormGroup>
              {editScopesValue.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Needs at least one scope.
                </Alert>
              )}
            </Box>
          )}

          {manageTab === 1 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                Callback URL
              </Typography>
              <TextField
                label="Callback URL"
                fullWidth
                size="small"
                value={editCallbackUrl}
                onChange={(e) => setEditCallbackUrl(e.target.value)}
                placeholder="https://your-agent.example.com/webhooks"
                helperText={
                  manageClient?.callbackUrl
                    ? 'Clear and save to stop all callbacks.'
                    : 'Saving issues a signing secret, shown once.'
                }
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                Event subscriptions
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Lead and appointment changes, from any source. Delivery callbacks are always on.
              </Typography>

              {!manageClient?.callbackUrl ? (
                <Alert severity="info">
                  Save a callback URL first — there is nowhere to deliver events until then.
                </Alert>
              ) : (
                <>
                  {RECORD_EVENT_GROUPS.map((group) => (
                    <Box key={group} mb={2}>
                      <Typography variant="overline" color="text.secondary">
                        {group}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      {RECORD_EVENTS.filter((e) => e.group === group).map((meta) => {
                        const sub = eventSub(meta.event);
                        const checked = !!sub;
                        return (
                          <Box
                            key={meta.event}
                            sx={{
                              mb: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: checked ? 'action.hover' : 'transparent',
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                              <FormControlLabel
                                sx={{ minWidth: 210, mr: 0 }}
                                control={
                                  <Checkbox
                                    checked={checked}
                                    disabled={meta.unavailable}
                                    onChange={() => toggleEvent(meta.event)}
                                  />
                                }
                                label={
                                  // The explanation moved onto a tooltip rather than being
                                  // deleted: seven stacked paragraphs made the panel unreadable,
                                  // but the distinction between e.g. created and re-engaged still
                                  // needs to be discoverable at the moment of choosing.
                                  <Tooltip title={meta.hint} placement="right">
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        color={meta.unavailable ? 'text.disabled' : 'text.primary'}
                                      >
                                        {meta.label}
                                        {meta.unavailable ? ' (coming soon)' : ''}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontFamily="monospace"
                                      >
                                        {meta.event}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                }
                              />
                              {checked && (
                                <Select
                                  size="small"
                                  value={sub?.scope ?? 'own'}
                                  onChange={(e) =>
                                    patchEvent(meta.event, { scope: e.target.value as 'own' | 'all' })
                                  }
                                  sx={{ minWidth: 190, mt: 0.5 }}
                                >
                                  <MenuItem value="own">Related to me</MenuItem>
                                  <MenuItem value="all">All events</MenuItem>
                                </Select>
                              )}
                              {checked && meta.fieldOptions && (
                                <Autocomplete
                                  multiple
                                  freeSolo
                                  size="small"
                                  options={meta.fieldOptions}
                                  value={sub?.fields ?? []}
                                  onChange={(_e, value) =>
                                    patchEvent(meta.event, { fields: value as string[] })
                                  }
                                  sx={{ flex: 1, minWidth: 240, mt: 0.5 }}
                                  renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                      <Chip
                                        size="small"
                                        label={option}
                                        {...getTagProps({ index })}
                                        key={option}
                                      />
                                    ))
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...(params as any)}
                                      placeholder={sub?.fields?.length ? '' : 'Any field'}
                                      helperText="Only fire when one of these changes"
                                    />
                                  )}
                                />
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                  {/* Phrased positively and stored negatively. The stored field is
                      `suppressSelfCausedEvents`, but "don't send X" as a checkbox makes the
                      DEFAULT state an unticked box that still delivers — a double negative the
                      reader has to unpick. Ticked-means-on matches the actual default, so the
                      inversion lives here rather than in the API. */}
                  <FormControlLabel
                    sx={{ mt: 1, mb: 1, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={!editSuppressSelf}
                        onChange={(e) => setEditSuppressSelf(!e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Subscribe to my own actions</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sync this CRM&apos;s own updates back too — untick if it loops.
                        </Typography>
                      </Box>
                    }
                  />
                  {editEventsValue.some((sub) => sub.scope === 'all') && (
                    // Downgraded from a full Alert to one line: the information still matters,
                    // but a banner this large read as an error on a perfectly valid choice.
                    <Typography variant="caption" color="warning.main" display="block">
                      &ldquo;All events&rdquo; streams every matching record in the account,
                      including ones this CRM never created.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {manageTab === 2 && (
            <Box>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    API key
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {manageClient?.mode === 'test'
                      ? 'Test keys expire 1 hour after creation or rotation.'
                      : 'The old secret keeps working for 72 hours.'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Autorenew />}
                    disabled={manageClient?.status === 'revoked'}
                    onClick={guard('apiAccess', () => {
                      const client = manageClient!;
                      closeManage();
                      setConfirmAction({ type: 'rotate', client });
                    })}
                  >
                    Rotate API key
                  </Button>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Callback signing secret
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {manageClient?.callbackUrl
                      ? 'No grace period — update your receiver immediately.'
                      : 'Issued when you first set a callback URL.'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={!manageClient?.callbackUrl || manageClient?.status === 'revoked'}
                    onClick={guard('apiAccess', () => {
                      const client = manageClient!;
                      closeManage();
                      setConfirmAction({ type: 'regenerate-callback-secret', client });
                    })}
                  >
                    Regenerate signing secret
                  </Button>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="error">
                    Revoke CRM
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Permanently cuts off access. This cannot be undone.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Block />}
                    disabled={manageClient?.status === 'revoked'}
                    onClick={guard('apiAccess', () => {
                      const client = manageClient!;
                      closeManage();
                      setConfirmAction({ type: 'revoke', client });
                    })}
                  >
                    Revoke
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {manageTab === 1 && manageClient?.callbackUrl && (
            <Button
              onClick={() => setEditEventsValue([])}
              disabled={editEventsSaving || editEventsValue.length === 0}
            >
              Clear events
            </Button>
          )}
          <Box flex={1} />
          <Button onClick={closeManage} disabled={editSaving || editScopesSaving || editEventsSaving}>
            {manageTab === 2 ? 'Close' : 'Cancel'}
          </Button>
          {manageTab === 0 && (
            <Button
              variant="contained"
              onClick={guard('apiAccess', handleSaveScopes)}
              disabled={editScopesSaving || editScopesValue.length === 0}
            >
              {editScopesSaving ? <CircularProgress size={20} /> : 'Save permissions'}
            </Button>
          )}
          {manageTab === 1 && (
            <>
              <Button
                variant={manageClient?.callbackUrl ? 'outlined' : 'contained'}
                onClick={guard('apiAccess', handleSaveCallback)}
                disabled={editSaving || editCallbackUrl.trim() === (manageClient?.callbackUrl ?? '')}
              >
                {editSaving ? <CircularProgress size={20} /> : 'Save URL'}
              </Button>
              {manageClient?.callbackUrl && (
                <Button
                  variant="contained"
                  onClick={guard('apiAccess', handleSaveEvents)}
                  disabled={editEventsSaving}
                >
                  {editEventsSaving ? <CircularProgress size={20} /> : 'Save events'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm rotate/revoke */}
      <Dialog open={!!confirmAction} onClose={() => !actionLoading && setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction?.type === 'rotate'
            ? 'Rotate API key?'
            : confirmAction?.type === 'regenerate-callback-secret'
              ? 'Regenerate signing secret?'
              : 'Revoke CRM?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmAction?.type === 'rotate'
              ? confirmAction.client.mode === 'test'
                ? `A new secret will be issued for "${confirmAction.client.name}". This is a test key, so it doesn't get the usual 72h grace window — both the old and new secret stop working 1 hour from now, when this key expires.`
                : `A new secret will be issued for "${confirmAction.client.name}". The old secret keeps working for 72 hours so you can redeploy safely.`
              : confirmAction?.type === 'regenerate-callback-secret'
                ? `A new signing secret will be issued for "${confirmAction.client.name}"'s callback. Unlike rotating the API key, there's no grace period — the old secret stops verifying immediately, so update it on your receiving end right after.`
                : `"${confirmAction?.client.name}" will immediately lose access. This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmAction?.type === 'revoke' ? 'error' : 'primary'}
            onClick={guard('apiAccess', handleConfirmAction)}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={20} />
            ) : confirmAction?.type === 'rotate' ? (
              'Rotate'
            ) : confirmAction?.type === 'regenerate-callback-secret' ? (
              'Regenerate'
            ) : (
              'Revoke'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExternalGatewayConfig;
